<script lang="ts">
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '../../editable';
	import PageGrid16 from '../../components/pageGrid16.svelte';
	import SbRichtext from '../../components/sbRichtext.svelte';
	import type {
		SbColor,
		SbImage,
		SbMuxVideo,
		SbOverlayPercentage,
		SbResponsiveMuxVideo
	} from '../../types';
	import Blok from '../../blok.svelte';
	import { getFontColor, getSecondaryFontColor } from './helpers';

	interface Props {
		content: ISbStoryData<{
			title: string;
			headline: string;
			copy: ISbRichtext;
			color: Extract<SbColor, 'brown-6' | 'green-1' | 'green-2' | 'green-3' | 'green-6'>;
			asset: Array<ISbStoryData<SbImage | SbMuxVideo | SbResponsiveMuxVideo>['content']>;
			info_cards: Array<ISbStoryData<any>['content']>;
			mobile_overlay: SbOverlayPercentage;
			desktop_overlay: SbOverlayPercentage;
			bottom_card_asset: Array<
				ISbStoryData<SbImage | SbMuxVideo | SbResponsiveMuxVideo>['content']
			>;
			bottom_card_mobile_overlay: SbOverlayPercentage;
			bottom_card_desktop_overlay: SbOverlayPercentage;
			bottom_card_headline: string;
			bottom_card_copy: ISbRichtext;
		}>['content'];
	}

	let { content }: Props = $props();
</script>

<section use:editable={content} class="my-12">
	<PageGrid16 margin gridClass="gap-y-3">
		<div class="col-span-full text-center lg:col-start-4 lg:col-end-[14]">
			<h1
				class="mb-7 text-balance text-[36px]/[112%] tracking-[-.36px] text-brown-1 lg:mb-13 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
			>
				{content.title}
			</h1>
		</div>
		<div
			class={[
				'col-span-full rounded-xl lg:col-start-2 lg:col-end-9 lg:flex lg:h-0 lg:min-h-full lg:flex-col',
				`bg-${content.color}`
			]}
		>
			<div class="px-7 py-7 lg:px-8 lg:py-8">
				<h2
					class={[
						'mb-2 text-[24px]/[100%] tracking-[-.24px] max-lg:text-center lg:text-[32px]/[110%] lg:tracking-[-.32px]',
						getFontColor(content.color)
					]}
				>
					{content.headline}
				</h2>
				<div
					class={[
						'text-balance text-[14px]/[160%] max-lg:text-center lg:text-[16px]/[150%]',
						getSecondaryFontColor(content.color)
					]}
				>
					<SbRichtext text={content.copy} />
				</div>
			</div>
			{#if content.asset?.length > 0}
				<div class="relative mx-5 mb-7 lg:mb-6 lg:flex lg:h-full">
					<div class="lg:h-0 lg:min-h-full lg:flex-shrink lg:basis-full">
						<Blok content={content.asset?.[0]} />
						{#if content.mobile_overlay !== '0'}
							<div
								class={[
									'pointer-events-none absolute inset-0 bg-black lg:hidden',
									`opacity-${content.mobile_overlay}`
								]}
							></div>
						{/if}
						{#if content.desktop_overlay !== '0'}
							<div
								class={[
									'pointer-events-none absolute inset-0 bg-black max-lg:hidden',
									`opacity-${content.desktop_overlay}`
								]}
							></div>
						{/if}
					</div>
				</div>
			{/if}
		</div>
		<div class="col-span-full flex h-fit flex-wrap gap-3 lg:col-start-9 lg:col-end-[16] lg:gap-4">
			{#each content.info_cards as card}
				<div class="aspect-4/3 basis-[calc(50%-6px)] lg:basis-[calc(50%-8px)]">
					<Blok content={card} />
				</div>
			{/each}
			<div class="basis-full">
				<article class="relative aspect-2/3 overflow-hidden rounded-xl lg:aspect-3/2">
					{#if content.bottom_card_asset?.length > 0}
						<Blok content={content.bottom_card_asset[0]} />
					{/if}
					{#if content.bottom_card_mobile_overlay !== '0'}
						<div
							class={[
								'pointer-events-none absolute inset-0 bg-black lg:hidden',
								`opacity-${content.bottom_card_mobile_overlay}`
							]}
						></div>
					{/if}
					{#if content.bottom_card_desktop_overlay !== '0'}
						<div
							class={[
								'pointer-events-none absolute inset-0 bg-black max-lg:hidden',
								`opacity-${content.bottom_card_desktop_overlay}`
							]}
						></div>
					{/if}
					<div class="absolute inset-x-0 bottom-0 p-5 text-white">
						<h1
							class="mb-2 text-[24px]/[108%] tracking-[-.24px] lg:text-[32px]/[110%] lg:tracking-[-.32px]"
						>
							{content.bottom_card_headline}
						</h1>
						<div class="text-balance text-[14px]/[160%] lg:text-[16px]/[150%]">
							<SbRichtext text={content.bottom_card_copy} />
						</div>
					</div>
				</article>
			</div>
		</div>
	</PageGrid16>
</section>
